import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";
import { loadGoogleAds, disableGoogleAds } from "@/lib/googleAds";

const STORAGE_KEY = "cookie-consent";
const OPEN_EVENT = "cookie-consent:open";

export type ConsentChoice = "accepted" | "rejected" | null;

/** Read current consent choice from localStorage. */
export const getConsent = (): ConsentChoice => {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "accepted" || v === "rejected" ? v : null;
};

/** Programmatically reopen the cookie banner (e.g. from "Cookie settings" link). */
export const openCookieSettings = () => {
  window.dispatchEvent(new Event(OPEN_EVENT));
};

const CookieConsent = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (existing === null) {
      setOpen(true);
    } else if (existing === "accepted") {
      loadGoogleAds();
    }

    const handler = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  const accept = () => {
    window.localStorage.setItem(STORAGE_KEY, "accepted");
    loadGoogleAds();
    setOpen(false);
  };

  const reject = () => {
    window.localStorage.setItem(STORAGE_KEY, "rejected");
    disableGoogleAds();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground">We use cookies</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              We use essential cookies to run FixItMigo, and — with your permission — Google Ads
              cookies to measure ad performance and reach people who may find us useful. See our{" "}
              <Link to="/cookies" className="underline hover:text-foreground">
                Cookie Policy
              </Link>
              .
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={accept}>
                Accept all
              </Button>
              <Button size="sm" variant="outline" onClick={reject}>
                Reject non-essential
              </Button>
              <Button size="sm" variant="ghost" asChild>
                <Link to="/cookies">Learn more</Link>
              </Button>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={reject}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;